import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { trackLoginGateShown, type LoginGateTrigger } from '@/lib/posthog';

/**
 * A.6.6c — paid cloud work needs an okraPDF account.
 *
 * The API answers any anonymous request that would spend money with
 * `401 { error: 'login_required', message }`. The web gates those actions
 * BEFORE the request (see `useLoginGate`); this module is the shared fallback
 * for when the server says no first, plus the single place that opens Clerk's
 * sign-up modal outside React.
 */

export const LOGIN_REQUIRED_ERROR = 'login_required';

/** Server copy, mirrored for the preflight gate and for bodies without a message. */
export const LOGIN_REQUIRED_MESSAGE =
  'Sign in to run this. Cloud processing needs an okraPDF account.';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * The human message for a `401 login_required` response, or `null` for any
 * other status/body. `data` is the parsed JSON body.
 */
export function loginRequiredMessage(status: unknown, data: unknown): string | null {
  if (status !== 401 || !isRecord(data)) return null;
  if (data.error !== LOGIN_REQUIRED_ERROR && data.code !== LOGIN_REQUIRED_ERROR) return null;
  return typeof data.message === 'string' && data.message.trim()
    ? data.message
    : LOGIN_REQUIRED_MESSAGE;
}

/** True when an RTK Query / unwrap() error is the `login_required` contract. */
export function isLoginRequiredError(error: unknown): boolean {
  return isRecord(error) && loginRequiredMessage(error.status, error.data) !== null;
}

// ── Sign-up opener bridge ──────────────────────────────────────────
// Clerk's `openSignUp` lives in React context; RTK Query base queries and raw
// fetch callers do not. `ClerkTokenSync` registers the opener (same pattern as
// the token getter). With Clerk disabled (no-key dev boot, self-host) the
// opener stays null and prompting is a no-op.

let signUpOpener: (() => void) | null = null;

export function setLoginPromptOpener(fn: (() => void) | null) {
  signUpOpener = fn;
}

export type LoginPromptContext = {
  /** Where the gate fired: 'tools', 'studio', 'extract', 'inbox', 'api'. */
  surface: string;
  /** Tool slug or endpoint name. */
  tool: string;
};

/**
 * Record `login_gate_shown` and open the sign-up modal. Returns false when no
 * opener is registered (Clerk disabled), so callers can fall back to copy.
 */
export function promptLogin(context: LoginPromptContext, trigger: LoginGateTrigger): boolean {
  trackLoginGateShown({ surface: context.surface, tool: context.tool, trigger });
  if (!signUpOpener) return false;
  signUpOpener();
  return true;
}

/**
 * Raw-fetch helper: if `response` is a `401 login_required`, prompt sign-up
 * and return the human message; otherwise `null`. Reads a CLONE, so the
 * caller can still consume the body.
 */
export async function handleLoginRequiredResponse(
  response: Response,
  context: LoginPromptContext,
): Promise<string | null> {
  if (response.status !== 401) return null;
  const data: unknown = await response
    .clone()
    .json()
    .catch(() => null);
  const message = loginRequiredMessage(response.status, data);
  if (message === null) return null;
  promptLogin(context, 'server_401');
  return message;
}

/**
 * Wrap an RTK Query base query so a `401 login_required` opens sign-up
 * instead of surfacing as a generic failure. The error keeps `status: 401`
 * and `code: 'login_required'`, but `data.error` is rewritten to the human
 * message: every runner renders `data.error` verbatim, so this one rewrite
 * replaces the raw `login_required` token with the server copy everywhere.
 */
export function withLoginRequiredPrompt(
  baseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>,
  surface: string,
): BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> {
  return async (args, api, extraOptions) => {
    const result = await baseQuery(args, api, extraOptions);
    const error = result.error;
    if (!error) return result;
    const message = loginRequiredMessage(error.status, error.data);
    if (message === null) return result;
    promptLogin({ surface, tool: api.endpoint }, 'server_401');
    return {
      ...result,
      error: {
        status: 401,
        data: {
          ...(isRecord(error.data) ? error.data : {}),
          error: message,
          code: LOGIN_REQUIRED_ERROR,
          message,
        },
      },
    };
  };
}
